import { useNavigate } from 'react-router-dom';
import { usePredictionMarketEventsFindLobby } from '@/lib/endpoints';
import { TRENDING_CATEGORY_CODE, useAppSearchParams } from '@/hooks/useAppSearchParams';
import { useRecentSearches, useClearRecentSearches } from '@/store/searchHistoryStore';
import { Skeleton } from '@/components/ui';
import { SearchRow } from './SearchRow';

/** Top events shown under "Popular Searches". Interim source: the lobby feed. */
const POPULAR_LIMIT = 6;

function SectionTitle({ title, onClearAll }: { title: string; onClearAll?: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <h2 className="m-0 text-[18px] font-medium text-ink leading-normal">{title}</h2>
      {onClearAll && (
        <button
          type="button"
          onClick={onClearAll}
          className="bg-transparent border-none cursor-pointer p-0 text-[16px] text-purple underline"
        >
          Clear All
        </button>
      )}
    </div>
  );
}

function RowSkeletons({ count }: { count: number }) {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-[56px] w-full rounded-[12px]" />
      ))}
    </div>
  );
}

export interface SearchEntryStateProps {
  /** Re-run a recent query by loading it back into the search field. */
  onPickRecent: (query: string) => void;
}

/**
 * The search page before a query is typed (ARC-113 follow-up): the player's
 * "Recent Searches" (when any, with a Clear All), followed by "Popular Searches"
 * (top lobby events). Recent rows re-run the query; popular rows open the event.
 */
export function SearchEntryState({ onPickRecent }: SearchEntryStateProps) {
  const navigate = useNavigate();
  const recent = useRecentSearches();
  const clearRecent = useClearRecentSearches();

  const { categoryCode } = useAppSearchParams();
  const categoryFilter = categoryCode !== TRENDING_CATEGORY_CODE ? categoryCode : undefined;
  const { data, isLoading } = usePredictionMarketEventsFindLobby({ categoryCode: categoryFilter, limit: POPULAR_LIMIT });
  const popular = data?.events ?? [];

  return (
    <div className="w-full px-3.5 md:px-0 pb-6 flex flex-col gap-6">
      {recent.length > 0 && (
        <section className="flex flex-col gap-3.5">
          <SectionTitle title="Recent Searches" onClearAll={clearRecent} />
          <div className="search-stagger flex flex-col gap-2">
            {recent.map((query) => (
              <SearchRow key={query} label={query} onClick={() => onPickRecent(query)} data-testid="search-recent-row" />
            ))}
          </div>
        </section>
      )}

      <section className="flex flex-col gap-3.5">
        <SectionTitle title="Popular Searches" />
        {isLoading ? (
          <RowSkeletons count={POPULAR_LIMIT} />
        ) : popular.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted">Nothing popular right now.</p>
        ) : (
          <div className="search-stagger flex flex-col gap-2">
            {popular.map((event) => (
              <SearchRow
                key={event.id}
                label={event.name}
                onClick={() => navigate(`/events/${event.id}`)}
                data-testid="search-popular-row"
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
